import { SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  PlaceInterface,
  SaleItemInterface,
  CategoryInterface,
  StoreInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
  OptionItemInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { placeValidations } from '../../../../../../common/utils/constants'
import { useInput } from '../../../../../../common/hooks/useInput'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'
import { createSelectList } from '../../../../../../common/utils/functions/createSelectList'

const validations: ValidationRulesInterface[] = placeValidations

export const useForm = (
  data: PlaceInterface,
  setData: React.Dispatch<React.SetStateAction<PlaceInterface>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  el?: PlaceInterface,
) => {
  const [salesApi] = useState(getSalesApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[]>([])
  const [disabled, setDisabled] = useState(true)
  const [view, setView] = useState<'main' | 'booking' | 'availability'>('main')

  const [allItems, setAllItems] = useState<SaleItemInterface[]>([])
  const [allCategories, setAllCategories] = useState<CategoryInterface[]>([])
  const [storeList, setStoreList] = useState<StoreInterface[]>([])

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )
  const { inputHandler } = useInput(setData)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()

  const getStoreList = useCallback(async () => {
    try {
      const result = await salesApi.getStoreList()
      if (result) setStoreList(result?.data || [])
    } catch (error) {}
  }, [salesApi])

  const loadItems = useCallback(async () => {
    try {
      const result = await salesApi.getSaleItemList()
      if (result) setAllItems(result.data)
    } catch (error) {}
  }, [salesApi])

  const loadCategories = useCallback(async () => {
    try {
      const result = await salesApi.getCategoryList()
      if (result) setAllCategories(result.data)
    } catch (error) {}
  }, [salesApi])

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const createStoreSelectList = useCallback(() => {
    return createSelectList(storeList)
  }, [storeList])

  const createItemSelectList = useCallback((): OptionItemInterface[] => {
    return allItems.map((item) => ({
      id: String(item.id),
      label: item.name,
      name: item.name,
      value: String(item.id),
    }))
  }, [allItems])

  const createCategorySelectList = useCallback((): OptionItemInterface[] => {
    return allCategories.map((cat) => ({
      id: String(cat.id),
      label: cat.name,
      name: cat.name,
      value: String(cat.id),
    }))
  }, [allCategories])

  useEffect(() => {
    if (el) {
      setData({
        ...el,
        itemIds: el.placeItems?.map((item) => item.itemId) || [],
        categoryIds: el.placeCategories?.map((cat) => cat.categoryId) || [],
      })
    }
  }, [el, setData])

  useEffect(() => {
    getStoreList()
    loadItems()
    loadCategories()
  }, [getStoreList, loadItems, loadCategories])

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(1000)

      const {
        id,
        createdAt,
        updatedAt,
        uuid,
        clusterId,
        deleted,
        placeItems,
        placeCategories,
        ...rest
      } = data

      if (edit && data.id) {
        await salesApi.updatePlace({ ...rest, id: data.id })
      } else {
        await salesApi.createPlace(rest)
      }

      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
      console.error('Form action error:', error)
    }
  }, [salesApi, data, edit, getList, setModalActive, showCommonLoader, hideCommonLoader])

  useEffect(() => {
    for (const item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (const item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item])
      }
    }
  }, [data])

  useEffect(() => {
    setDisabled(validationResult.some((el) => el.notValid))
  }, [validationResult])

  return {
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    view,
    setView,
    createStoreSelectList,
    createItemSelectList,
    createCategorySelectList,
  }
}
